import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface AreaForm {
  name: string;
  type: string;
  description?: string;
  location: {
    description: string;
  };
}

interface Props {
  onComplete: () => void;
}

const StartWithArea: React.FC<Props> = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AreaForm>();

  const onSubmit = async (data: AreaForm) => {
    setLoading(true);
    setError('');

    try {
      await api.post('/onboarding/start-with-area', data);
      toast.success('🏡 Your area has been created!');
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create area');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>
          🏡
        </Typography>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Define Your Area
        </Typography>
        <Typography variant="body1" color="text.secondary">
          An area is a section within a space that groups related gardens
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          label="Area Name"
          margin="normal"
          {...register('name', {
            required: 'Please name your area',
          })}
          error={!!errors.name}
          helperText={errors.name?.message || 'e.g., "West Side Planters", "Patio Garden Zone", "Front Yard Beds"'}
        />

        <FormControl fullWidth margin="normal" error={!!errors.type}>
          <InputLabel>Area Type</InputLabel>
          <Select
            defaultValue=""
            {...register('type', { required: 'Please select an area type' })}
          >
            <MenuItem value="patio">Patio</MenuItem>
            <MenuItem value="balcony">Balcony</MenuItem>
            <MenuItem value="frontyard_section">Front Yard Section</MenuItem>
            <MenuItem value="backyard_section">Backyard Section</MenuItem>
            <MenuItem value="side_yard">Side Yard</MenuItem>
            <MenuItem value="rooftop_section">Rooftop Section</MenuItem>
            <MenuItem value="common_area">Common Area</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
          {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
        </FormControl>

        <TextField
          fullWidth
          label="Location Description"
          margin="normal"
          {...register('location.description', {
            required: 'Please describe where this area is located',
          })}
          error={!!errors.location?.description}
          helperText={
            errors.location?.description?.message ||
            'e.g., "West side of the rooftop", "Along the north fence", "Next to the patio door"'
          }
          multiline
          rows={2}
        />

        <TextField
          fullWidth
          label="Description (Optional)"
          margin="normal"
          multiline
          rows={3}
          {...register('description')}
          helperText="What makes this area special? What do you plan to grow here?"
        />

        <Box mt={4}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create My Area'}
          </Button>
        </Box>
      </form>

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          💡 Tip: Areas help organize multiple gardens in the same location, like grouping all your balcony planters!
        </Typography>
      </Box>
    </Paper>
  );
};

export default StartWithArea;